/**
 * MobileJoinQr — the invitation QR an invited person scans with OS Buddy on
 * their phone (#1244 slice E4, design §5.4).
 *
 * The value is the `mobileJoinUrl` staff-api mints on invite and resend
 * (design §4.1): an https URL whose entire payload — including the one-time
 * invitation token — lives in the **fragment**, so it never reaches a portal,
 * ingress or CDN access log. That shapes what this component may do with it:
 *
 * - it renders the URL as a QR and prints it, and never as copyable text. A URL
 *   on the clipboard is one sync away from leaving the workshop, so the
 *   invitation *web* link stays the copyable one. (The `/m/join` landing page
 *   does now exist — the invitation email links to it — so a pasted link would
 *   resolve; that makes the clipboard argument the whole argument rather than
 *   half of it.)
 * - it is never logged and never sent anywhere. It lives in props for as long
 *   as the dialog is open, and nowhere else.
 *
 * The token is hashed at rest, so the code can only be rendered at mint time.
 * Re-displaying it later necessarily goes through resend, which rotates the
 * token — see the "show mobile code" action in TeamMembersList.
 *
 * **Sizing is derived from the encoded code, not assumed.** Percent-encoding
 * inflates every non-ASCII character sixfold, and the fragment carries
 * `tenants.name` (varchar 255), `tenants.slug` (varchar 100) and
 * `user_invitations.email` (varchar 255) — so the payload varies by a factor of
 * four across real tenants. Measured; module counts include the 8-module quiet
 * zone, so the QR version is `(modules - 8 - 17) / 4`. The last column is
 * px/module on the *narrowest* screen the code can appear on — a 320 px phone,
 * which leaves it 248 px (see NARROWEST_CONTENT_PX) — because that is what
 * decides whether it scans, not the width a desktop happens to give it.
 *
 * | payload | chars | modules | version | px/module at 248 px |
 * |---|---|---|---|---|
 * | typical                                   | 229 | 69  | 11 | 3.6 |
 * | long Finnish name, truncated to 48        | 309 | 77  | 13 | 3.2 |
 * | worst reachable: column-max slug + email  | 835 | 117 | 23 | 2.1 |
 *
 * staff-api truncates `name=` to 48 characters (MOBILE_JOIN_NAME_MAX), which is
 * what keeps the middle row scannable: untruncated, a 255-character Finnish
 * workshop name took the URL to 663 characters and 105 modules — 2.4 px per
 * module, which a phone camera does not read reliably. The remaining dense row
 * needs a 100-character slug *and* a 255-character address, neither of which
 * occurs in practice.
 *
 * This component nonetheless sizes from the encoded code rather than trusting
 * that bound, because it renders whatever it is handed. Too dense for a screen
 * and the admin is told to print it — print scales with the module count and
 * stays over 0.5 mm per module for every code the encoder accepts, so paper
 * always works even when the screen does not.
 *
 * Past the encoder's 2331-byte ECC-M ceiling `QRCodeSVG` throws rather than
 * degrading, and a throw here would tear down the success view *after* the
 * invitation was committed, losing the only copy of the token. With the
 * truncation in place that ceiling is far out of reach (the worst reachable
 * payload is 835 bytes), so the guard below is defence-in-depth against a
 * widened column or a new fragment field — not a live failure.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeSVG } from 'qrcode.react';
import { CopyableCode } from '@/components/common/CopyableCode';
import { formatDate } from '@/utils/locale';

/**
 * Byte capacity of a version-40 QR at ECC level M. `QRCodeSVG` throws on
 * anything longer, so this is checked before it renders.
 */
const QR_MAX_BYTES = 2331;

/** Preferred on-screen density; comfortable for a phone camera on a monitor. */
const PREFERRED_PX_PER_MODULE = 3.5;

/** Below this, scanning off a screen gets unreliable and we say so. */
const MIN_PX_PER_MODULE = 3;

/**
 * Upper bound on the *target* width, so a dense code does not dominate a wide
 * dialog. The code is laid out at `width: 100%` under this cap, so its real
 * width is whatever the container gives it and it can never overflow — which is
 * the actual guarantee. A fixed pixel width could report an adequate
 * px/module while clipping its quiet zone on a narrow phone.
 */
const MAX_TARGET_PX = 420;

/** Never shrink below this, however few modules the code has. */
const MIN_TARGET_PX = 176;

/**
 * Width the code can actually occupy on the narrowest phone we support: a
 * 320 px viewport, less `DialogContent`'s 24 px of padding each side and the
 * white frame's 12 px each side. The density warning is decided against *this*
 * rather than the target, so it is truthful on the smallest screen the QR can
 * appear on instead of only on a desktop.
 */
const NARROWEST_CONTENT_PX = 320 - 48 - 24;

/** Printed density. 0.7 mm/module reads comfortably off paper. */
const PRINT_MM_PER_MODULE = 0.7;

/** Printable width inside the sheet's 16 mm margins on A4. */
const MAX_PRINT_MM = 150;

const MIN_PRINT_MM = 40;

/** Nominal size handed to the encoder; the real size is applied as CSS. */
const NOMINAL_QR_PX = 256;

/** The white frame's `p: 1.5` — 12 px each side. */
const FRAME_PADDING_PX = 24;

/** Four modules of quiet zone, as the QR spec requires. The library defaults to 0. */
const QR_MARGIN_MODULES = 4;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface MobileJoinQrProps {
  /** `mobileJoinUrl` from the invite / resend response. */
  mobileJoinUrl: string;
  /** The invited address, shown so the admin can confirm whose code this is. */
  email: string;
  /** ISO-8601 invitation expiry, straight from the same response. */
  expiresAt: string;
  /**
   * Heading above the code. Defaults to the invitation wording.
   *
   * Overridable because #1244 E6 reuses this component for the **login** QR,
   * which is a different thing entirely — a session for whoever scans it first,
   * rather than an invitation to create an account.
   */
  heading?: string;
  /**
   * Replaces the "single-use, expires <date>" line. E6 passes a live countdown:
   * a *date* is meaningless for a code that lives 90 seconds, and a stale-looking
   * "expires 28.7.2026" would read as "this is good all day".
   */
  caption?: ReactNode;
  /**
   * Whether to offer printing. E6 turns it **off**: printing a 90-second
   * credential produces paper that is worthless within the minute and a bearer
   * token lying on a shop floor if it is not. Printing is right for an invitation
   * (valid for days, meant to be handed over) and wrong for a login code.
   */
  enablePrint?: boolean;
  /**
   * Whether to show the payload as copyable text.
   *
   * **Off by default and opt-in per call site**, so each surface weighs it — not
   * because any one answer is obviously right. A scanned QR reaches only the phone's
   * camera; a copied code sits on a clipboard that may sync to every device signed
   * into the same iCloud account.
   *
   * Both current callers enable it, for the same underlying reason — there is no
   * other route on the device the user is actually holding:
   *
   *  - an **invitation** is read in a mail client that may rewrite the link, so
   *    copy-and-paste is the only route that survives Gmail;
   *  - a **login code** is often minted while reading the portal *on the phone*,
   *    where there is no second device to scan with.
   *
   * An earlier version withheld it from the login code on the grounds that a live
   * session is worse than an invitation token. That had the risk backwards: TTL is
   * the dominant term, and 90 seconds of clipboard exposure for a code the user just
   * minted for themselves behind a password step-up is a *smaller* window than seven
   * days for an invitation. It also left the phone-only case with no path at all.
   */
  enableCopy?: boolean;
  /**
   * Replaces the default copy hint, which is worded for an invitation ("if the link
   * does not open the app…"). A login code is not a link, so the login section passes
   * its own wording.
   */
  copyHelperText?: string;
}

export function MobileJoinQr({
  mobileJoinUrl,
  email,
  expiresAt,
  heading,
  caption,
  enablePrint = true,
  enableCopy = false,
  copyHelperText,
}: MobileJoinQrProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const qrRef = useRef<HTMLDivElement>(null);

  /** Modules across, quiet zone included — read from what the encoder produced. */
  const [modules, setModules] = useState<number | null>(null);

  const encodedBytes = new TextEncoder().encode(mobileJoinUrl).length;
  const exceedsCapacity = encodedBytes > QR_MAX_BYTES;

  useLayoutEffect(() => {
    if (exceedsCapacity) {
      setModules(null);
      return;
    }
    const viewBox = qrRef.current?.querySelector('svg')?.getAttribute('viewBox');
    const measured = Number(viewBox?.split(' ')[2]);
    setModules(Number.isFinite(measured) && measured > 0 ? measured : null);
  }, [mobileJoinUrl, exceedsCapacity]);

  const targetPx = modules
    ? clamp(modules * PREFERRED_PX_PER_MODULE, MIN_TARGET_PX, MAX_TARGET_PX)
    : NOMINAL_QR_PX;
  const printMm = modules
    ? clamp(modules * PRINT_MM_PER_MODULE, MIN_PRINT_MM, MAX_PRINT_MM)
    : MIN_PRINT_MM;

  /**
   * True when the narrowest supported screen cannot give the code
   * MIN_PX_PER_MODULE. Judged against the smallest container it can appear in,
   * not the target width — a code that scans on a monitor but not on a phone
   * still deserves the "print it" note.
   */
  const isDense =
    modules !== null && Math.min(targetPx, NARROWEST_CONTENT_PX) / modules < MIN_PX_PER_MODULE;

  const appName = tc('appName');
  const expiryNote = t('mobileJoinQr.expiryNote', {
    date: formatDate(expiresAt),
  });
  const instruction = t('mobileJoinQr.instruction', { app: appName });
  const codeHeading = heading ?? t('mobileJoinQr.heading');

  /**
   * Print by serializing the rendered SVG into a new window, the same
   * window.open + static HTML pattern the work-order and invoice print views
   * use. The SVG carries a viewBox, so the print stylesheet sizes it in
   * millimetres — scaled to the module count — without re-encoding the payload.
   */
  const handlePrint = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (!printWindow) return;

    const svgMarkup = new XMLSerializer().serializeToString(svg);
    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(t('mobileJoinQr.printTitle', { app: appName }))}</title>
    <style>
      @page { margin: 16mm; }
      body {
        font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
        margin: 0;
        text-align: center;
      }
      h1 { font-size: 18px; margin: 0 0 6px; }
      p { font-size: 13px; margin: 4px 0; }
      .qr { width: ${printMm.toFixed(1)}mm; height: ${printMm.toFixed(1)}mm; margin: 10mm auto; }
      .qr svg { width: 100%; height: 100%; }
      .expiry { font-size: 12px; }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(t('mobileJoinQr.printTitle', { app: appName }))}</h1>
    <p>${escapeHtml(instruction)}</p>
    <div class="qr">${svgMarkup}</div>
    <p><strong>${escapeHtml(email)}</strong></p>
    <p class="expiry">${escapeHtml(expiryNote)}</p>
  </body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  /**
   * Past the encoder's capacity there is no QR to show. Rendering one anyway
   * throws inside React and takes the whole success view down with it, so the
   * admin would lose the invitation link too — the one thing that still works.
   */
  if (exceedsCapacity) {
    return (
      <Alert severity="warning" sx={{ width: '100%' }}>
        {t('mobileJoinQr.tooLarge')}
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
      <Typography variant="subtitle2">{codeHeading}</Typography>

      {/*
        A white frame under the code. The library's default black-on-white ink is
        kept deliberately and is *not* theme-aware: scanners want dark modules on
        a light ground, an inverted code is rejected by many readers, and the
        printed sheet has to be black on white regardless of the portal theme.
      */}
      <Box
        ref={qrRef}
        // Inline rather than `sx` so the computed cap is readable in a test.
        style={{ maxWidth: `${String(Math.round(targetPx + FRAME_PADDING_PX))}px` }}
        sx={{
          width: '100%',
          bgcolor: 'common.white',
          p: 1.5,
          borderRadius: 1,
          lineHeight: 0,
        }}
      >
        <QRCodeSVG
          value={mobileJoinUrl}
          size={NOMINAL_QR_PX}
          marginSize={QR_MARGIN_MODULES}
          level="M"
          title={codeHeading}
          // Fills the frame, so the rendered width is whatever the container
          // allows and the quiet zone can never be clipped.
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" textAlign="center">
        {instruction}
      </Typography>
      {caption ?? (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {expiryNote}
        </Typography>
      )}

      {isDense && (
        <Alert severity="info" sx={{ width: '100%' }}>
          {t('mobileJoinQr.dense')}
        </Alert>
      )}

      {enableCopy && (
        <CopyableCode
          value={mobileJoinUrl}
          copyLabel={t('mobileJoinQr.copyCode')}
          copiedLabel={t('mobileJoinQr.codeCopied')}
          helperText={copyHelperText ?? t('mobileJoinQr.copyHint', { app: appName })}
        />
      )}

      {enablePrint && (
        <Button size="small" startIcon={<PrintIcon />} onClick={handlePrint}>
          {t('mobileJoinQr.print')}
        </Button>
      )}
    </Stack>
  );
}
